import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  getAccessRequest,
  updateRequests,
  exportExcel,
} from "../../network/cipAPI";
import RequestStatusFilterHeader from "./customFilter";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AgTable from "../../components/AgTable";
import DupontIconButton from "../../components/DupontIconButton";
import DupontButton from "../../components/DupontButton";
import { OverlayLoader } from "../../components/PageLoading";
import Footer from "../../common/footer/Footer";
import { getRequestStatusText, formatToLocalDateTime } from "../../utils/Utils";
import Header from "../../components/Header";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import "./index.scss";

const AdminPage = () => {
  const { t } = useTranslation();
  const { token } = useSelector((state) => state);

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      // Export type set to 'request_access'
      const type = "request_access";
      const response = await exportExcel(type);
      if (response && response.data) {
        // Use backend filename if provided, else fallback
        let fileName = t("adminPage.accessRequestsFile");
        if (response.headers && response.headers["content-disposition"]) {
          const match = response.headers["content-disposition"].match(
            /filename="?([^";]+)"?/,
          );
          if (match && match[1]) fileName = match[1];
        }
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const fetchAccessRequests = async () => {
    setShowOverLayLoader(true);
    try {
      const response = await getAccessRequest(
        currentPage,
        pageSize,
        currentFilters,
        sortField,
        sortOrder,
      );
      if (response && response.data) {
        setRowData(response.data.users || []);
        setTotalPages(response.data.total_pages || 1);
      } else {
        setRowData([]);
        setTotalPages(1);
      }
    } catch (error) {
      setRowData([]);
      setTotalPages(1);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  useEffect(() => {
    if (token) fetchAccessRequests();
  }, [currentPage, pageSize, currentFilters, sortField, sortOrder, token]);

  const onSortChanged = (event) => {
    const sortModel = event.columnApi
      .getAllColumns()
      .map((column) => {
        return {
          colId: column.getColId(),
          sort: column.getSort(),
        };
      })
      .filter((col) => col.sort);

    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      setSortField(colId);
      setSortOrder(sort);
    } else {
      setSortField("created_at");
      setSortOrder("desc");
    }
  };

  const StatusButtonsCellRenderer = (props) => {
    const { request_status, email } = props.data;
    const handleButtonClick = async (requestStatus) => {
      const payload = {
        user_email: email,
        request_status: requestStatus,
        comments: props.data.comments,
      };
      setShowOverLayLoader(true);
      try {
        await updateRequests(payload);
        fetchAccessRequests();
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setShowOverLayLoader(false);
      }
    };
    return (
      <div className="status-buttons">
        {(request_status === 1 || request_status === 4) && (
          <>
            <DupontButton onClick={() => handleButtonClick(2)}>
              {t("adminPage.accept")}
            </DupontButton>
            <DupontButton
              customClass="small-red-btn"
              onClick={() => handleButtonClick(3)}
            >
              {t("adminPage.reject")}
            </DupontButton>
          </>
        )}
        {(request_status === 2 || request_status === 3) && (
          <DupontButton
            customClass="small-yellow-btn"
            onClick={() => handleButtonClick(4)}
          >
            {t("adminPage.revoke")}
          </DupontButton>
        )}
      </div>
    );
  };

  const onRequestStatusFilterChange = (filterQuery) => {
    const filters = filterQuery ? filterQuery.split(",") : [];
    setCurrentFilters(filters);
  };

  const colDefs = [
    {
      headerName: t("adminPage.userid"),
      field: "id",
      minWidth: 100,
      sortable: false,
    },
    {
      headerName: t("adminPage.userName"),
      field: "name",
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: t("adminPage.company"),
      field: "company",
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: t("adminPage.country"),
      field: "country",
      minWidth: 150,
      sortable: false,
    },
    {
      headerName: t("adminPage.email"),
      field: "email",
      minWidth: 250,
      sortable: false,
    },
    {
      headerName: t("adminPage.created_at"),
      field: "created_at",
      minWidth: 200,
      sortable: true,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
    },
    {
      headerName: t("adminPage.request_status"),
      field: "request_status",
      minWidth: 250,
      sortable: false,
      headerComponent: RequestStatusFilterHeader,
      headerComponentParams: {
        onFilterChange: onRequestStatusFilterChange,
        currentFilters: currentFilters,
      },
      valueGetter: (params) =>
        getRequestStatusText(t, params.data.request_status),
    },
    {
      headerName: t("adminPage.comments"),
      field: "comments",
      minWidth: 150,
      sortable: false,
      cellEditorPopup: true,
      editable: true,
      cellEditor: "agLargeTextCellEditor",
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      },
      tooltipValueGetter: (params) => {
        return params.value;
      },
    },
    {
      headerName: t("adminPage.actions"),
      field: "actions",
      minWidth: 300,
      cellRenderer: StatusButtonsCellRenderer,
      sortable: false,
    },
  ];

  const onCellValueChanged = async (event) => {
    const { data } = event;
    const payload = {
      user_email: data.email,
      request_status: data.request_status,
      comments: data.comments,
    };
    setShowOverLayLoader(true);
    try {
      await updateRequests(payload);
      fetchAccessRequests();
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  return (
    <div className="container-main">
      {showOverLayLoader && <OverlayLoader customHeight={600} />}
      <Header />
      <div className="main-container">
        <div className="content-container">
          <div className="admin-title">
            <span>{t("adminPage.manageAcess")}</span>
            <DupontButton onClick={handleExportExcel} disabled={exportLoading}>
              {exportLoading
                ? t("adminPage.exporting")
                : t("adminPage.exportExcel")}
            </DupontButton>
          </div>
          <div className="descriptionText marginBtm10">
            {t("adminPage.createdAtInfoText")}
          </div>

          <div>
            <AgTable
              columnDefs={colDefs}
              rowData={rowData}
              customHeight="55vh"
              rowHeight={50}
              onSortChanged={onSortChanged}
              onCellValueChanged={onCellValueChanged}
              hideExport
            />
            <div className="pagination-controls">
              <label htmlFor="pageSize" className="page-size-label">
                {t("adminPage.pageSize")}
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => {
                  setCurrentPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="page-size-select"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <DupontIconButton
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn prev-btn"
                tooltip={t("adminPage.previous")}
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </DupontIconButton>
              <span className="pagination-info">
                {t("adminPage.pageInfo", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
              <DupontIconButton
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className="pagination-btn next-btn"
                tooltip={t("adminPage.next")}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </DupontIconButton>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AdminPage;
